import React, { useEffect, useState, useCallback } from "react"
import PropTypes from "prop-types"
import { Spinner } from "reactstrap"
import axios from "axios"
import { getInboxFull, reenviarCorreo } from "../api"
import { formatFechaHora, estadoConfig, formatNumber } from "../helpers"

/**
 * Drawer drill-down de un CUFE con error.
 * Consume GET /a7/inbox/{cufe}/full y muestra timeline + acciones.
 *
 * Para reenviar usa POST /a7/reenviar con fallback a
 * POST timbrador.ls-da.com/v2/factura/resendMail si /a7 responde 404
 * (misma lógica que la pantalla de Comprobantes).
 */
const DrawerDetalle = ({ abierto, cufe, onCerrar }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [reenviando, setReenviando] = useState(false)
  const [reenvioMsg, setReenvioMsg] = useState(null)

  const cargar = useCallback(async () => {
    if (!abierto || !cufe) return
    setLoading(true)
    setError("")
    setData(null)
    try {
      const r = await getInboxFull(cufe)
      const body = r.data
      if (body?.success === false) throw new Error(body.error || body.message || "Error en el servidor")
      setData(body?.data || body)
    } catch (err) {
      setError(err.response?.data?.error || err.message || "No se pudo cargar el detalle.")
    } finally {
      setLoading(false)
    }
  }, [abierto, cufe])

  useEffect(() => { cargar() }, [cargar])

  useEffect(() => {
    if (abierto) {
      document.body.style.overflow = "hidden"
      return () => { document.body.style.overflow = "" }
    }
  }, [abierto])

  const handleReenviar = async () => {
    if (!cufe || reenviando) return
    setReenviando(true)
    setReenvioMsg(null)
    let usadoLegacy = false
    try {
      try {
        await reenviarCorreo({ cufe })
      } catch (err) {
        if (err.response?.status === 404) {
          // eslint-disable-next-line no-console
          console.warn("Reenvío /a7/reenviar devolvió 404, intentando legacy…")
          // El legacy espera { cufe, mail } — sin mailReceptor lo dejamos al destinatario original
          await axios.post(
            "https://timbrador.ls-da.com/v2/factura/resendMail",
            { cufe },
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: "eyJhbGciOiJIUzUxMiJ9.eyJzdWIiOiJzb2Z0bG9neSIsImF1ZGllbmNlIjoid2ViIiwiY3JlYXRlZCI6MTczMjIxMTc5MTEyMCwiZXhwIjoyNzMyMjExNzkwfQ.q6_8G2cViYb3l7NU5R9gAWNo1JQz-afRkc-mPnuc9n2iIYUJlbq6XHzm-K_0ldnQmmkxFbRofiXkVAB41_Me5Q",
              },
            },
          )
          usadoLegacy = true
        } else {
          throw err
        }
      }
      setReenvioMsg({
        tipo: "ok",
        texto: usadoLegacy ? "Correo reenviado con éxito (servicio legacy)." : "Correo reenviado con éxito.",
      })
      // Refrescamos el detalle para ver el nuevo intento
      cargar()
    } catch (err) {
      setReenvioMsg({
        tipo: "error",
        texto: err.response?.data?.error?.message || err.response?.data?.message || err.message || "Error al reenviar.",
      })
    } finally {
      setReenviando(false)
    }
  }

  if (!abierto) return null

  const docData = data?.documento || {}
  const estado = data?.estado_actual || {}
  const destinatarios = data?.destinatarios || []
  const estadoCfg = estadoConfig(estado.estado)

  return (
    <>
      <div onClick={onCerrar} style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)",
        zIndex: 1040, backdropFilter: "blur(2px)",
      }} />
      <aside style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: "min(960px, 95vw)",
        background: "#f8fafc", zIndex: 1041,
        display: "flex", flexDirection: "column",
        boxShadow: "-12px 0 40px rgba(15,23,42,0.18)",
        animation: "slideIn .25s ease",
      }}>
        <style>{`@keyframes slideIn { from { transform: translateX(40px); opacity: .6 } to { transform: translateX(0); opacity: 1 } }`}</style>

        {/* Header */}
        <div style={{
          background: "#fff", borderBottom: "1px solid #e2e8f0",
          padding: "14px 20px", display: "flex", alignItems: "center", gap: 14,
        }}>
          <div style={{
            width: 44, height: 44, borderRadius: 10,
            background: `${estadoCfg.color}1a`, color: estadoCfg.color,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 20, flexShrink: 0,
          }}>
            <i className={`fas ${estadoCfg.icon}`} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4 }}>
              Detalle del CUFE
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {docData.prefijo || ""}{docData.folio || ""} {docData.tipo_documento && `· ${docData.tipo_documento}`}
            </div>
            <div style={{ fontSize: 11, color: "#64748b", fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {cufe}
            </div>
          </div>
          <button type="button" className="btn btn-sm btn-outline-primary me-2"
            onClick={handleReenviar} disabled={reenviando || loading}>
            {reenviando ? <><Spinner size="sm" className="me-1" /> Reenviando…</> : <><i className="fas fa-paper-plane me-1" /> Reenviar</>}
          </button>
          <button type="button" onClick={onCerrar} style={{
            background: "none", border: "1px solid #e2e8f0", borderRadius: 8,
            color: "#475569", cursor: "pointer", padding: "6px 10px", fontSize: 13,
          }}>
            <i className="mdi mdi-close me-1" />Cerrar
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
          {loading && (
            <div style={{ padding: 60, textAlign: "center", color: "#64748b" }}>
              <Spinner color="primary" />
              <div className="mt-2">Cargando detalle…</div>
            </div>
          )}

          {error && !loading && (
            <div style={{
              padding: "12px 16px", background: "#fef2f2", color: "#991b1b",
              border: "1px solid #fecaca", borderRadius: 10,
              display: "flex", alignItems: "center", gap: 10,
            }}>
              <i className="mdi mdi-alert-circle" />
              <span style={{ flex: 1 }}>{error}</span>
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={cargar}>Reintentar</button>
            </div>
          )}

          {reenvioMsg && (
            <div style={{
              padding: "10px 14px", marginBottom: 12,
              background: reenvioMsg.tipo === "ok" ? "#dcfce7" : "#fef2f2",
              border: `1px solid ${reenvioMsg.tipo === "ok" ? "#bbf7d0" : "#fecaca"}`,
              color: reenvioMsg.tipo === "ok" ? "#166534" : "#991b1b",
              borderRadius: 8, fontSize: 13, display: "flex", alignItems: "center", gap: 8,
            }}>
              <i className={`mdi ${reenvioMsg.tipo === "ok" ? "mdi-check-circle" : "mdi-alert-circle"}`} />
              <span>{reenvioMsg.texto}</span>
              <button type="button" className="btn-close ms-auto" style={{ fontSize: 10 }}
                onClick={() => setReenvioMsg(null)} />
            </div>
          )}

          {data && !loading && !error && (
            <>
              {/* Resumen rápido del documento */}
              <div style={{
                background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10,
                padding: 14, marginBottom: 12,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 12, color: "#475569" }}>
                      <strong>{docData.nombre_emisor || "—"}</strong>{docData.sucursal && <> · {docData.sucursal}</>}
                    </div>
                    <div style={{ fontSize: 12, color: "#475569", marginTop: 2 }}>
                      Receptor: <strong>{docData.nombre_receptor || "—"}</strong>
                      {docData.nit_receptor && <> · NIT {docData.nit_receptor}</>}
                    </div>
                  </div>
                  <span style={{
                    padding: "4px 12px", borderRadius: 999,
                    background: estadoCfg.color, color: "#fff",
                    fontSize: 12, fontWeight: 700,
                  }}>
                    <i className={`fas ${estadoCfg.icon} me-1`} />
                    {(estado.estado || "—").toUpperCase()}
                  </span>
                </div>
                {estado.ultimo_error && (
                  <div style={{ fontSize: 12, color: "#dc2626", marginTop: 8 }}>
                    <i className="mdi mdi-alert-circle-outline me-1" />
                    <strong>Último error:</strong> {estado.ultimo_error}
                  </div>
                )}
              </div>

              {/* Timeline por destinatario */}
              {destinatarios.map((dest, idxD) => {
                const tl = dest.timeline || []
                const ok = String(dest.estado_envio_actual || "").toLowerCase() === "sent"
                return (
                  <div key={idxD} style={{
                    background: "#fff", border: "1px solid #e2e8f0",
                    borderLeft: `4px solid ${ok ? "#16a34a" : "#dc2626"}`,
                    borderRadius: 10, padding: "14px 18px", marginBottom: 12,
                  }}>
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3 pb-2"
                      style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <div>
                        <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", fontWeight: 700 }}>
                          <i className="fas fa-envelope me-1" />Destinatario #{idxD + 1}
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", wordBreak: "break-all" }}>
                          {dest.email}
                        </div>
                      </div>
                      <span style={{
                        padding: "3px 10px", borderRadius: 999,
                        background: ok ? "#16a34a" : "#dc2626", color: "#fff",
                        fontSize: 11, fontWeight: 700,
                      }}>
                        <i className={`fas ${ok ? "fa-check" : "fa-times"} me-1`} />
                        {(dest.estado_envio_actual || "—").toUpperCase()}
                      </span>
                    </div>

                    {tl.length === 0 ? (
                      <div className="text-muted small text-center py-2">Sin eventos en la línea de tiempo.</div>
                    ) : (
                      <div style={{ position: "relative", paddingLeft: 36 }}>
                        <div style={{
                          position: "absolute", left: 14, top: 8, bottom: 8,
                          width: 2, background: "linear-gradient(to bottom, #e2e8f0, #cbd5e1, #e2e8f0)",
                        }} />
                        {tl.map((ev, idxE) => {
                          const stMap = {
                            success: { color: "#16a34a", bg: "#dcfce7", border: "#16a34a" },
                            error:   { color: "#dc2626", bg: "#fee2e2", border: "#dc2626" },
                            warning: { color: "#d97706", bg: "#fef3c7", border: "#d97706" },
                            info:    { color: "#0284c7", bg: "#e0f2fe", border: "#0284c7" },
                          }
                          const st = stMap[String(ev.nivel || "").toLowerCase()] || { color: "#64748b", bg: "#f1f5f9", border: "#94a3b8" }
                          const atParts = String(ev.at || "").split(" ")
                          const atHora = atParts[1] ? atParts[1].substring(0, 8) : ""
                          const atFecha = atParts[0] || ""
                          return (
                            <div key={idxE} style={{ position: "relative", marginBottom: 12 }}>
                              <div style={{
                                position: "absolute", left: -36, top: 0,
                                width: 30, height: 30, borderRadius: "50%",
                                background: st.bg, border: `2px solid ${st.border}`,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: 14, lineHeight: 1, zIndex: 1,
                              }}>{ev.icon || "•"}</div>
                              <div style={{
                                background: "#fff", border: `1px solid ${st.border}40`,
                                borderLeft: `3px solid ${st.border}`,
                                borderRadius: 8, padding: "8px 12px",
                              }}>
                                <div className="d-flex justify-content-between align-items-start flex-wrap gap-1">
                                  <div style={{ flex: 1, minWidth: 200 }}>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: st.color }}>
                                      {ev.titulo || ev.tipo || "Evento"}
                                    </div>
                                    {ev.subtitulo && (
                                      <div style={{ fontSize: 11, color: "#475569", marginTop: 3, wordBreak: "break-word" }}>
                                        {ev.subtitulo}
                                      </div>
                                    )}
                                  </div>
                                  <div style={{ textAlign: "right", minWidth: 90 }}>
                                    <div style={{ fontSize: 11, color: "#0f172a", fontWeight: 600, fontFamily: "monospace" }}>
                                      {atHora || "—"}
                                    </div>
                                    {atFecha && <div style={{ fontSize: 9, color: "#94a3b8" }}>{atFecha}</div>}
                                  </div>
                                </div>
                                {(ev.actor || ev.tipo) && (
                                  <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
                                    {ev.actor && (
                                      <span style={{
                                        padding: "1px 8px", borderRadius: 999,
                                        background: "#f1f5f9", color: "#475569",
                                        fontSize: 10, fontWeight: 600,
                                      }}>{ev.actor}</span>
                                    )}
                                    {ev.tipo && (
                                      <span style={{
                                        padding: "1px 8px", borderRadius: 999,
                                        background: st.bg, color: st.color,
                                        fontSize: 10, fontWeight: 700, fontFamily: "monospace",
                                      }}>{ev.tipo}</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}

                    <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 10, display: "flex", gap: 12, flexWrap: "wrap" }}>
                      <span>Intentos: <strong>{formatNumber(dest.intentos)}</strong></span>
                      {dest.ultimo_envio_at && <span>Último envío: <strong>{formatFechaHora(dest.ultimo_envio_at)}</strong></span>}
                    </div>
                  </div>
                )
              })}
            </>
          )}
        </div>
      </aside>
    </>
  )
}

DrawerDetalle.propTypes = {
  abierto: PropTypes.bool,
  cufe: PropTypes.string,
  onCerrar: PropTypes.func.isRequired,
}

export default DrawerDetalle
